import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchData } from '../fetchapi/fetchapi';
import { parseProjectPath, DEFAULT_BRANCH } from '../utils/projectPath';
import { getUiView } from '../hooks/useUiView';

// AUTH = IDENTITY ONLY. The active project + branch are NOT part of auth anymore —
// they live in the URL (single source of truth: /p/<slug>/b/<branch>/...), are read
// from the path by ProjectContext, and sent to the backend as X-Cortex-Project /
// X-Cortex-Branch headers on each request. This context no longer bootstraps a
// project, no longer touches the ?project= query param, and no longer re-signs the
// JWT on project/branch switches.

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Verify stored token on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setIsAuthenticated(false);
        setUser(null);
        return;
      }

      // Verify token by calling /api/auth/me (identity only).
      const data = await fetchData('auth/me', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (data && data.id) {
        setUser(data);
        setIsAuthenticated(true);
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Identity- and scope-carrying keys that must NEVER survive a user switch
  // on a shared browser (Ken incident 2026-07-28: a stale cortex_acting_org +
  // flow-studio history from the previous user steered a fresh account into
  // another org's project context). UX prefs (theme, sidebar state) survive.
  const STALE_SESSION_KEYS = [
    'cortex_acting_org', 'currentProjectId', 'currentId', 'prevIds',
    'historyProvider', 'channelUuid',
  ];
  const purgeStaleSessionKeys = () => {
    for (const k of STALE_SESSION_KEYS) {
      try { localStorage.removeItem(k); } catch {}
    }
  };

  const login = async (credentials) => {
    try {
      const data = await fetchData('auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });

      if (data.access_token) {
        // Different user than the one this browser last held? Drop their
        // leftover scope/navigation state before seeding the new session.
        try {
          const prev = JSON.parse(localStorage.getItem('user_data') || 'null');
          if (!prev || prev.email !== data.user?.email) purgeStaleSessionKeys();
        } catch { purgeStaleSessionKeys(); }
        localStorage.setItem('access_token', data.access_token);
        const u = data.user || null;
        if (u) {
          localStorage.setItem('user_data', JSON.stringify(u));
          setUser(u);
        }
        setIsAuthenticated(true);
        return { success: true, user: u };
      }

      return { success: false, error: 'No access token received' };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: error?.message || 'Login failed' };
    }
  };

  const signup = async ({ email, password, first_name, last_name }) => {
    try {
      const data = await fetchData('auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, first_name, last_name })
      });

      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        if (data.user) {
          localStorage.setItem('user_data', JSON.stringify(data.user));
          setUser(data.user);
        }
        setIsAuthenticated(true);
        return { success: true, user: data.user };
      }

      return { success: false, error: 'No access token received' };
    } catch (error) {
      console.error('Signup failed:', error);
      return { success: false, error: error?.message || 'Signup failed' };
    }
  };

  // Switch project/branch = change the URL (the single source of truth). The
  // AuthProvider sits ABOVE the router, so it navigates via window.location; the
  // new path drives the X-Cortex-Project / X-Cortex-Branch headers and every page
  // re-fetches under the new project. No JWT re-sign, no storage of the project.
  // Fallback page when the current URL carries none: Standard mode's home is
  // Apps (Dashboard is hidden from its nav); Advanced keeps Dashboard.
  const homePage = () => (getUiView() === 'simple' ? 'apps' : 'dashboard');

  const switchProject = async (project) => {
    try {
      const { rest } = parseProjectPath(window.location.pathname);
      window.location.assign(`/p/${encodeURIComponent(project)}/b/${DEFAULT_BRANCH}/${rest || homePage()}`);
      return { success: true };
    } catch (error) {
      console.error('Project switch failed:', error);
      return { success: false, error: error?.message || 'Project switch failed' };
    }
  };

  const switchBranch = async (branch) => {
    try {
      const { projectSlug, rest } = parseProjectPath(window.location.pathname);
      if (!projectSlug) return { success: false, error: 'No project in URL' };
      window.location.assign(`/p/${encodeURIComponent(projectSlug)}/b/${encodeURIComponent(branch)}/${rest || homePage()}`);
      return { success: true };
    } catch (error) {
      console.error('Branch switch failed:', error);
      return { success: false, error: error?.message || 'Branch switch failed' };
    }
  };

  const logout = async () => {
    try {
      await fetchData('auth/logout', {
        method: 'POST'
      });
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      purgeStaleSessionKeys();
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    signup,
    logout,
    checkAuthStatus,
    switchBranch,
    switchProject,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
